import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../../shared/lib/supabaseClient';
import { useAuth } from '../../../auth/context/AuthContext';
import { toast } from 'react-hot-toast';
import NonTickerConfirmationDialog from './NonTickerConfirmationDialog';
import { BASE_URL } from '../../../../shared/lib/api';

/** Supported broker CSV formats; backend normalizer key. */
export type BrokerCsvType = 'etrade' | 'schwab' | 'fidelity' | 'coinbase' | 'vanguard';

interface BrokerCsvConfig {
  title: string;
  helpParagraphs: string[];
}

const BROKER_CSV_CONFIG: Record<BrokerCsvType, BrokerCsvConfig> = {
  etrade: {
    title: 'Upload E*TRADE CSV',
    helpParagraphs: [
      'Expected headers (case-insensitive): TransactionDate, TransactionType, SecurityType, Symbol, Quantity, Amount, Price, Commission, Description.',
      'E*TRADE exports often place headers on row 4 and data starting on row 6. Our importer automatically detects the header row if it appears within the first 4 rows.',
    ],
  },
  schwab: {
    title: 'Upload Schwab CSV',
    helpParagraphs: [
      'Expected headers (case-insensitive): Date, Action, Symbol, Description, Quantity, Price, Fees & Comm, Amount.',
      'Export your transaction history from Charles Schwab as CSV. Our importer detects the header row within the first 10 rows.',
    ],
  },
  fidelity: {
    title: 'Upload Fidelity CSV',
    helpParagraphs: [
      'Expected headers (case-insensitive): Run Date, Account, Account Number, Action, Symbol, Description, Type, Quantity, Price, Commission, Fees, Amount, Settlement Date.',
      'Export your transaction history from Fidelity as CSV. Rows where the first column (Run Date) is not a date—such as disclaimer text at the bottom—are automatically ignored.',
    ],
  },
  coinbase: {
    title: 'Upload Coinbase CSV',
    helpParagraphs: [
      'Expected headers: ID, Timestamp, Transaction Type, Asset, Quantity Transacted, Price Currency, Price at Transaction, Subtotal, Total (inclusive of fees and/or spread), Fees and/or Spread, Notes.',
      'Coinbase CSVs place headers on row 4 after a preamble. Our importer detects the header row automatically. Supported transaction types: Buy, Sell, Convert, Reward Income, Retail Staking Transfer, Retail Unstaking Transfer. Staking Income rows are excluded.',
    ],
  },
  vanguard: {
    title: 'Upload Vanguard CSV',
    helpParagraphs: [
      'Expected transaction-history headers: Account Number, Trade Date, Settlement Date, Transaction Type, Transaction Description, Investment Name, Symbol, Shares, Share Price, Principal Amount, Commissions and Fees, Net Amount, Accrued Interest, Account Type.',
      'Vanguard exports may include a holdings summary before transaction history. Our importer detects the transaction header and ignores summary, blank, and footer rows.',
    ],
  },
};

interface Props {
  isOpen: boolean;
  accountId: string | null;
  broker: BrokerCsvType;
  onClose: () => void;
  onRefreshImpacted2?: (accountId: string, hints: { tickers: string[]; dividendTickers: string[]; cash_changed: boolean }) => Promise<void> | void;
  autoCloseOnSuccess?: boolean;
  onSuccess?: (hints: { tickers: string[]; dividendTickers: string[]; cash_changed: boolean; newTickers?: string[] }, warnings?: { message: string; invalid_tickers: string[] }) => void;
}

const BrokerCsvUploadModal: React.FC<Props> = ({
  isOpen,
  accountId,
  broker,
  onClose,
  onRefreshImpacted2,
  autoCloseOnSuccess,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const config = BROKER_CSV_CONFIG[broker];

  const [file, setFile] = useState<File | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showNonTickerDialog, setShowNonTickerDialog] = useState(false);
  const [invalidTickers, setInvalidTickers] = useState<string[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(null);
      setResult(null);
      setFile(null);
      setShowLoadingMessage(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (busy) {
      const timer = setTimeout(() => setShowLoadingMessage(true), 3000);
      return () => clearTimeout(timer);
    }
    setShowLoadingMessage(false);
  }, [busy]);

  const doUpload = async (fileToUpload: File, excludeTickers?: string[], includeTickers?: string[]) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const form = new FormData();
    form.append('file', fileToUpload);
    form.append('account_id', accountId!);
    form.append('broker', broker);
    if (excludeTickers?.length) {
      form.append('exclude_tickers', excludeTickers.join(','));
    }
    if (includeTickers?.length) {
      form.append('include_tickers', includeTickers.join(','));
    }
    const res = await fetch(`${BASE_URL}/csv/upload`, {
      method: 'POST',
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    const json = await res.json();
    return { res, json };
  };

  const handleSuccess = (json: { impacted?: { tickers?: string[]; dividendTickers?: string[]; div_changed?: string[]; cash_changed?: boolean }; recalculate?: { hints?: { tickers?: string[]; dividendTickers?: string[]; div_changed?: string[]; cash_changed?: boolean } }; new_tickers?: string[]; warnings?: { message: string; invalid_tickers: string[] } }) => {
    const hints = json?.impacted || json?.recalculate?.hints;
    const newTickers = json?.new_tickers ?? [];
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
      }
    }
    if (accountId && hints && onRefreshImpacted2) {
      const refreshResult = onRefreshImpacted2(accountId, {
        tickers: hints.tickers || [],
        dividendTickers: hints.dividendTickers || hints.div_changed || [],
        cash_changed: !!hints.cash_changed,
      });
      if (refreshResult instanceof Promise) {
        refreshResult.catch((e: unknown) => console.error('refreshImpactedData2 error', e));
      }
    }
    if (onSuccess) {
      onSuccess(
        {
          tickers: hints?.tickers || [],
          dividendTickers: hints?.dividendTickers || hints?.div_changed || [],
          cash_changed: !!hints?.cash_changed,
          ...(newTickers.length > 0 && { newTickers }),
        },
        json?.warnings
      );
    }
    if (autoCloseOnSuccess) {
      setResult(null);
      onClose();
    }
  };

  const upload = async () => {
    if (!file || !accountId) return;
    setBusy(true);
    setResult(null);
    setErrorMessage(null);
    try {
      const { res, json } = await doUpload(file);
      if (!autoCloseOnSuccess) {
        setResult(json as Record<string, unknown>);
      }
      if (res.ok) {
        if (json?.need_decision && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(file);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        toast.success('CSV uploaded and processed. Refreshing data...');
        handleSuccess(json);
      } else {
        if (json?.error_code === 'CSV_INVALID_TICKERS' && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(file);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        setErrorMessage('Error uploading CSV.');
        toast.error((json?.error as string) || 'Upload failed');
      }
    } catch (e) {
      setResult({ error: String(e) });
      setErrorMessage('Error uploading CSV.');
      toast.error(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleFileInputClick = () => {
    setIsFilePickerOpen(true);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
    setIsFilePickerOpen(false);
  };

  const handleNonTickerConfirm = async () => {
    if (!invalidTickers.length || !pendingFile) return;
    try {
      setShowNonTickerDialog(false);
      setBusy(true);
      // Re-upload with include_tickers so backend inserts txs and Phase 0 creates MANUAL + history
      const { res, json } = await doUpload(pendingFile, undefined, invalidTickers);
      if (res.ok) {
        if (json?.need_decision && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(pendingFile);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        toast.success('CSV uploaded and processed. Refreshing data...');
        handleSuccess(json);
      } else {
        setErrorMessage('Error uploading CSV after adding custom tickers.');
        toast.error((json?.error as string) || 'Upload failed');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add custom tickers. Please try again.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusy(false);
      setInvalidTickers([]);
      setPendingFile(null);
    }
  };

  const handleNonTickerCancel = () => {
    setShowNonTickerDialog(false);
    setInvalidTickers([]);
    setPendingFile(null);
    setBusy(false);
  };

  const handleExcludeFromUpload = async () => {
    if (!invalidTickers.length || !pendingFile) return;
    try {
      setShowNonTickerDialog(false);
      setBusy(true);
      const { res, json } = await doUpload(pendingFile, invalidTickers);
      if (res.ok) {
        if (json?.need_decision && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(pendingFile);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        toast.success('CSV uploaded and processed. Refreshing data...');
        handleSuccess(json);
      } else {
        if (json?.error_code === 'CSV_INVALID_TICKERS' && json?.invalid_tickers) {
          setInvalidTickers(json.invalid_tickers);
          setPendingFile(pendingFile);
          setShowNonTickerDialog(true);
          setBusy(false);
          return;
        }
        setErrorMessage('Error uploading CSV after excluding tickers.');
        toast.error((json?.error as string) || 'Upload failed');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to upload CSV. Please try again.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusy(false);
      setInvalidTickers([]);
      setPendingFile(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-black bg-opacity-30 ${isFilePickerOpen ? 'z-[100]' : 'z-[10000]'}`}>
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">{config.title}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        {errorMessage && (
          <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
            {errorMessage}
          </div>
        )}
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={handleFileInputClick}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Choose CSV File
          </button>
          {file && (
            <div className="text-sm text-gray-600">Selected: {file.name}</div>
          )}
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showHelp ? 'Hide' : 'Show'} file format help
          </button>
          {showHelp && (
            <div className="bg-gray-50 rounded p-3 text-sm text-gray-700">
              {config.helpParagraphs.map((p, i) => (
                <p key={i} className={i > 0 ? 'mb-2 mt-2' : 'mb-2'}>{p}</p>
              ))}
            </div>
          )}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={upload}
              disabled={!file || !accountId || busy}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {busy && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {busy ? 'Uploading...' : 'Upload'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
            {showLoadingMessage && <span className="text-sm text-gray-600 ml-2">This may take a second ...</span>}
          </div>
          {result && (
            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-64">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      </div>
      <NonTickerConfirmationDialog
        isOpen={showNonTickerDialog}
        tickers={invalidTickers}
        onConfirm={handleNonTickerConfirm}
        onCancel={handleNonTickerCancel}
        onExclude={handleExcludeFromUpload}
      />
    </div>
  );
};

export default BrokerCsvUploadModal;
